import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import moment from 'moment';
import ReactPaginate from 'react-paginate';
import {
    FormGroup, Input, Table, Alert, Button, Label,
} from 'reactstrap';

import {
    fetchExternalApiLogs, statusBadgeClass, STATUS_CLASSES, AUTH_METHODS,
    DEFAULT_LOGS_ENTRIES, MAX_LOGS_ENTRIES,
} from '../helpers/externalApiLogs';
import { PERIOD_PRESETS, presetRange, MAX_RANGE_DAYS } from '../helpers/externalApiStats';
import { formatMs, formatNumber } from '../helpers/format';
import { formatPoints } from '../helpers/externalApiCost';
import ClientAutocompleteInput from './clientAutocompleteInput';
import ExternalApiLogDetail from './externalApiLogDetail';
import CopyButton from './copyButton';
import { isKnownReplica, replicaLoadHint } from '../helpers/externalApiReplicas';

const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

const styles = {
    filterBar: {
        display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 14,
        backgroundColor: '#FFFFFF', borderRadius: 6, padding: '16px 18px',
        boxShadow: '0 10px 20px rgba(0,0,0,0.06)', marginBottom: 20,
    },
    filterGroup: { marginBottom: 0, minWidth: 140 },
    filterLabel: { fontSize: 11, fontWeight: 600, color: '#818FA5', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 },
    chipRow: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 },
    chip: {
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px',
        borderRadius: 16, backgroundColor: '#E6EDF5', fontSize: 13, color: '#3A3F56',
    },
    chipClose: { border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 14, color: '#818FA5', lineHeight: 1 },
    mono: { fontFamily: "'SFMono-Regular', Menlo, Monaco, Consolas, monospace", fontSize: 12 },
    tableCard: { backgroundColor: '#FFFFFF', borderRadius: 6, boxShadow: '0 10px 20px rgba(0,0,0,0.06)', padding: '4px 0 10px 0' },
    footer: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 18px', flexWrap: 'wrap', gap: 10 },
    emptyState: { textAlign: 'center', padding: '50px 20px', color: '#818FA5', fontSize: 14 },
};

// requestId gets its own always-visible search field below (it's the main
// way support finds a specific request), so it is not also a dismissible chip
const LINKED_FILTER_LABELS = {
    fingerprint: 'Fingerprint',
    bodyHash: 'Body hash',
    apiKeyId: 'API key',
    replica: 'Replica',
};

// filters a parent page can own instead of this component (see `scope`)
const SCOPE_KEYS = ['clientId', 'clientLabel', 'apiKeyId', 'apiKeyLabel', 'route', 'method', 'replica'];

const defaultFilters = {
    preset: '24h',
    customFrom: '',
    customTo: '',
    clientId: '',
    clientLabel: '',
    apiKeyId: '',
    apiKeyLabel: '',
    route: '',
    method: '',
    path: '',
    statusClass: '',
    status: '',
    minDurationMs: '',
    minDbQueries: '',
    fingerprint: '',
    requestId: '',
    bodyHash: '',
    ip: '',
    authMethod: '',
    authErrorCode: '',
    authenticated: '',
    replica: '',
    sortField: 'createdAt',
    sortType: -1,
    page: 0,
    entries: DEFAULT_LOGS_ENTRIES,
};

/** slot number, plus a marker on slow requests that hit a congested replica */
function ReplicaCell({ log, lang }) {
    if (!isKnownReplica(log.replica)) {
        return <span style={{ color: '#818FA5' }}>—</span>;
    }

    let hint = replicaLoadHint(log, lang);

    return (
        <span>
            {log.replica}
            {hint && hint.congested ? (
                <span title={hint.text} style={{ marginLeft: 6, fontSize: 11, fontWeight: 600, color: '#a86f00', whiteSpace: 'nowrap' }}>
                    ▲ {'congested'.translate(lang)}
                </span>
            ) : null}
        </span>
    );
}

/**
* External API request log (VD-725 follow-up). Used both as the standalone
* "External API Logs" page and as the "API logs" tab on the client detail
* page (pass `clientId`). Also the landing spot for every "open logs" link
* from the Insights panels.
*
* @param {object} [initialFilters] - seed values, applied on mount and again whenever `resetToken` changes
* @param {*} [resetToken] - bump this (any changing value) to re-apply a fresh `initialFilters`, e.g. a link from Insights
* @param {function} [onFiltersChange] - called with the full filters object whenever they change, so a parent can mirror them into the URL
* @param {object} [range] - {from, to} ISO strings owned by the parent page; hides this component's own period picker
* @param {object} [scope] - {clientId, clientLabel, apiKeyId, apiKeyLabel, route, method, replica} owned by the parent page; hides those filters here
* @param {function} [onScopeChange] - called with a patch of `scope` keys when something here narrows one of them
* @param {function} [onOpenClient] - makes the Client column clickable
*/
function ExternalApiLogs({ lang, clientId, initialFilters, resetToken, onFiltersChange, range: externalRange, scope, onScopeChange, onOpenClient }) {
    let [filters, setFilters] = useState({ ...defaultFilters, ...(initialFilters || {}) });
    let [pathDraft, setPathDraft] = useState(filters.path || '');
    let [requestIdDraft, setRequestIdDraft] = useState(filters.requestId || '');
    let [items, setItems] = useState([]);
    let [total, setTotal] = useState(0);
    let [loading, setLoading] = useState(true);
    let [error, setError] = useState(null);
    let [showMore, setShowMore] = useState(false);
    let [openLogId, setOpenLogId] = useState(null);
    let pathTimer = useRef(null);
    let requestIdTimer = useRef(null);
    let lastResetToken = useRef(resetToken);

    let updateFilters = useCallback((patch) => {
        setFilters((prev) => {
            let next = { ...prev, ...patch };
            if (onFiltersChange) onFiltersChange(next);
            return next;
        });
    }, [onFiltersChange]);

    // a link from Insights (or anywhere else) re-seeds the filters
    useEffect(() => {
        if (resetToken === undefined || resetToken === lastResetToken.current) return;
        lastResetToken.current = resetToken;
        let next = { ...defaultFilters, ...(initialFilters || {}) };
        setFilters(next);
        setPathDraft(next.path || '');
        setRequestIdDraft(next.requestId || '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetToken]);

    // debounce the Request ID quick-search the same way as path
    useEffect(() => {
        if (requestIdDraft === filters.requestId) return;
        if (requestIdTimer.current) clearTimeout(requestIdTimer.current);
        requestIdTimer.current = setTimeout(() => updateFilters({ requestId: requestIdDraft, page: 0 }), 300);
        return () => { if (requestIdTimer.current) clearTimeout(requestIdTimer.current); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestIdDraft]);

    useEffect(() => {
        if (pathDraft === filters.path) return;
        if (pathTimer.current) clearTimeout(pathTimer.current);
        pathTimer.current = setTimeout(() => updateFilters({ path: pathDraft, page: 0 }), 300);
        return () => { if (pathTimer.current) clearTimeout(pathTimer.current); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathDraft]);

    // the effective filters: the parent's scope (when it owns one) wins
    let f = scope ? { ...filters, ...scope } : filters;

    let applyFilterPatch = (patch) => {
        if (!scope || !onScopeChange) {
            updateFilters(patch);
            return;
        }

        let scoped = {};
        let own = {};
        Object.keys(patch).forEach((key) => { (SCOPE_KEYS.indexOf(key) !== -1 ? scoped : own)[key] = patch[key]; });
        if (Object.keys(scoped).length) onScopeChange(scoped);
        updateFilters({ ...own, page: 0 });
    };

    let ownRange = useMemo(() => {
        if (externalRange) return null;
        if (filters.preset !== 'custom') {
            return presetRange(filters.preset) || presetRange('24h');
        }

        let from = filters.customFrom ? moment(filters.customFrom).toISOString() : null;
        let to = filters.customTo ? moment(filters.customTo).toISOString() : null;

        return (from && to) ? { from, to } : null;
    }, [externalRange, filters.preset, filters.customFrom, filters.customTo]);

    let range = externalRange || ownRange;

    let rangeTooLong = useMemo(() => {
        if (!range) return false;
        return moment(range.to).diff(moment(range.from), 'days', true) > MAX_RANGE_DAYS;
    }, [range]);

    // a plain string, so a new-but-equal `f` object never triggers a refetch
    let requestKey = JSON.stringify({ ...f, clientId: clientId || f.clientId, range });

    // a new page-level period or scope starts from the first page again
    let scopeKey = JSON.stringify({ scope: scope || null, from: externalRange ? externalRange.from : null, to: externalRange ? externalRange.to : null });
    let lastScopeKey = useRef(scopeKey);
    useEffect(() => {
        if (scopeKey === lastScopeKey.current) return;
        lastScopeKey.current = scopeKey;
        if (Number(filters.page)) updateFilters({ page: 0 });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scopeKey]);

    useEffect(() => {
        if (!range) {
            setLoading(false);
            setItems([]);
            setTotal(0);
            return;
        }

        setLoading(true);
        setError(null);

        fetchExternalApiLogs({
            from: range.from,
            to: range.to,
            clientId: clientId || f.clientId || undefined,
            apiKeyId: f.apiKeyId || undefined,
            route: f.route || undefined,
            method: f.method || undefined,
            replica: f.replica,
            path: filters.path || undefined,
            status: filters.status || undefined,
            statusClass: !filters.status ? filters.statusClass || undefined : undefined,
            minDurationMs: filters.minDurationMs || undefined,
            minDbQueries: filters.minDbQueries || undefined,
            fingerprint: filters.fingerprint || undefined,
            requestId: filters.requestId || undefined,
            bodyHash: filters.bodyHash || undefined,
            ip: filters.ip || undefined,
            authMethod: filters.authMethod || undefined,
            authErrorCode: filters.authErrorCode || undefined,
            authenticated: filters.authenticated || undefined,
            sortField: filters.sortField,
            sortType: filters.sortType,
            page: filters.page,
            entries: filters.entries,
        }).then(({ items: result, total: resultTotal, error: err }) => {
            setLoading(false);
            if (err) {
                setError(err);
                setItems([]);
                setTotal(0);
            } else {
                setItems(result);
                setTotal(resultTotal);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestKey]);

    let totalPages = Math.max(1, Math.ceil(total / (Number(filters.entries) || DEFAULT_LOGS_ENTRIES)));
    // the API key / replica chips belong to the parent when it owns the scope
    let linkedChips = Object.keys(LINKED_FILTER_LABELS).filter((key) => filters[key] && !(scope && (key === 'apiKeyId' || key === 'replica')));

    return (
        <div>
            <div style={styles.filterBar}>
                {!externalRange ? (
                    <FormGroup style={styles.filterGroup}>
                        <div style={styles.filterLabel}>{'Period'.translate(lang)}</div>
                        <Input type="select" style={{ height: 42 }} value={filters.preset} onChange={(e) => updateFilters({ preset: e.target.value, page: 0 })}>
                            {PERIOD_PRESETS.map((p) => <option key={p.value} value={p.value}>{p.label.translate(lang)}</option>)}
                        </Input>
                    </FormGroup>
                ) : null}

                {!externalRange && filters.preset === 'custom' ? (
                    <>
                        <FormGroup style={styles.filterGroup}>
                            <div style={styles.filterLabel}>{'From'.translate(lang)}</div>
                            <Input type="datetime-local" style={{ height: 42 }} value={filters.customFrom} onChange={(e) => updateFilters({ customFrom: e.target.value, page: 0 })} />
                        </FormGroup>
                        <FormGroup style={styles.filterGroup}>
                            <div style={styles.filterLabel}>{'To'.translate(lang)}</div>
                            <Input type="datetime-local" style={{ height: 42 }} value={filters.customTo} onChange={(e) => updateFilters({ customTo: e.target.value, page: 0 })} />
                        </FormGroup>
                    </>
                ) : null}

                <FormGroup style={styles.filterGroup}>
                    <div style={styles.filterLabel} title={'Search by the X-Request-ID a client reports when filing a support ticket'.translate(lang)}>
                        {'Request ID'.translate(lang)}
                    </div>
                    <Input
                        type="text"
                        style={{ height: 42, minWidth: 220 }}
                        placeholder={'Paste a request ID...'.translate(lang)}
                        value={requestIdDraft}
                        onChange={(e) => setRequestIdDraft(e.target.value)}
                    />
                </FormGroup>

                {!clientId && !scope ? (
                    <FormGroup style={styles.filterGroup}>
                        <div style={styles.filterLabel}>{'Client'.translate(lang)}</div>
                        <ClientAutocompleteInput
                            lang={lang}
                            value={filters.clientId}
                            label={filters.clientLabel}
                            onSelect={(item) => updateFilters({ clientId: item._id, clientLabel: item.username, page: 0 })}
                            onClear={() => updateFilters({ clientId: '', clientLabel: '', page: 0 })}
                        />
                    </FormGroup>
                ) : null}

                {!scope ? (
                    <>
                        <FormGroup style={styles.filterGroup}>
                            <div style={styles.filterLabel}>{'Route'.translate(lang)}</div>
                            <Input type="text" style={{ height: 42, minWidth: 150 }} placeholder="/v2/orders" value={filters.route} onChange={(e) => updateFilters({ route: e.target.value, page: 0 })} />
                        </FormGroup>

                        <FormGroup style={styles.filterGroup}>
                            <div style={styles.filterLabel}>{'Method'.translate(lang)}</div>
                            <Input type="select" style={{ height: 42 }} value={filters.method} onChange={(e) => updateFilters({ method: e.target.value, page: 0 })}>
                                <option value="">{'All'.translate(lang)}</option>
                                {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                            </Input>
                        </FormGroup>
                    </>
                ) : null}

                <FormGroup style={styles.filterGroup}>
                    <div style={styles.filterLabel}>{'Path contains'.translate(lang)}</div>
                    <Input type="text" style={{ height: 42, minWidth: 150 }} placeholder="12345" value={pathDraft} onChange={(e) => setPathDraft(e.target.value)} />
                </FormGroup>

                <FormGroup style={styles.filterGroup}>
                    <div style={styles.filterLabel}>{'Status class'.translate(lang)}</div>
                    <Input type="select" style={{ height: 42 }} value={filters.statusClass} disabled={!!filters.status} onChange={(e) => updateFilters({ statusClass: e.target.value, page: 0 })}>
                        <option value="">{'All'.translate(lang)}</option>
                        {STATUS_CLASSES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </Input>
                </FormGroup>

                <FormGroup style={styles.filterGroup}>
                    <div style={styles.filterLabel}>{'Min duration (ms)'.translate(lang)}</div>
                    <Input type="number" style={{ height: 42, minWidth: 110 }} value={filters.minDurationMs} onChange={(e) => updateFilters({ minDurationMs: e.target.value, page: 0 })} />
                </FormGroup>

                <Button color="secondary" outline style={{ height: 42 }} onClick={() => setShowMore((v) => !v)}>
                    {showMore ? 'Fewer filters'.translate(lang) : 'More filters'.translate(lang)}
                </Button>

                <Button
                    color="secondary"
                    outline
                    style={{ height: 42 }}
                    onClick={() => { setPathDraft(''); updateFilters({ ...defaultFilters, preset: filters.preset }); }}
                >
                    {'Reset filters'.translate(lang)}
                </Button>

                {showMore ? (
                    <>
                        <FormGroup style={styles.filterGroup}>
                            <div style={styles.filterLabel}>{'Exact status'.translate(lang)}</div>
                            <Input type="number" style={{ height: 42, minWidth: 100 }} value={filters.status} onChange={(e) => updateFilters({ status: e.target.value, page: 0 })} />
                        </FormGroup>
                        <FormGroup style={styles.filterGroup}>
                            <div style={styles.filterLabel}>{'IP'.translate(lang)}</div>
                            <Input type="text" style={{ height: 42, minWidth: 130 }} value={filters.ip} onChange={(e) => updateFilters({ ip: e.target.value, page: 0 })} />
                        </FormGroup>
                        <FormGroup style={styles.filterGroup}>
                            <div style={styles.filterLabel}>{'Auth method'.translate(lang)}</div>
                            <Input type="select" style={{ height: 42 }} value={filters.authMethod} onChange={(e) => updateFilters({ authMethod: e.target.value, page: 0 })}>
                                <option value="">{'All'.translate(lang)}</option>
                                {AUTH_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                            </Input>
                        </FormGroup>
                        <FormGroup style={styles.filterGroup}>
                            <div style={styles.filterLabel}>{'Authenticated'.translate(lang)}</div>
                            <Input type="select" style={{ height: 42 }} value={filters.authenticated} onChange={(e) => updateFilters({ authenticated: e.target.value, page: 0 })}>
                                <option value="">{'All'.translate(lang)}</option>
                                <option value="true">{'Yes'.translate(lang)}</option>
                                <option value="false">{'No'.translate(lang)}</option>
                            </Input>
                        </FormGroup>
                        <FormGroup style={styles.filterGroup}>
                            <div style={styles.filterLabel}>{'Auth error code'.translate(lang)}</div>
                            <Input type="text" style={{ height: 42, minWidth: 150 }} value={filters.authErrorCode} onChange={(e) => updateFilters({ authErrorCode: e.target.value, page: 0 })} />
                        </FormGroup>
                        {!scope ? (
                            <FormGroup style={styles.filterGroup}>
                                <div style={styles.filterLabel}>{'Replica'.translate(lang)}</div>
                                <Input type="number" min={1} style={{ height: 42, minWidth: 90 }} value={filters.replica} onChange={(e) => updateFilters({ replica: e.target.value, page: 0 })} />
                            </FormGroup>
                        ) : null}
                        <FormGroup style={styles.filterGroup}>
                            <div style={styles.filterLabel} title={'e.g. "20" = more than 20 DB queries in that request'.translate(lang)}>{'Min DB queries'.translate(lang)}</div>
                            <Input type="number" style={{ height: 42, minWidth: 110 }} value={filters.minDbQueries} onChange={(e) => updateFilters({ minDbQueries: e.target.value, page: 0 })} />
                        </FormGroup>
                    </>
                ) : null}
            </div>

            {linkedChips.length ? (
                <div style={styles.chipRow}>
                    {linkedChips.map((key) => (
                        <div key={key} style={styles.chip}>
                            {LINKED_FILTER_LABELS[key].translate(lang)}: <span style={styles.mono}>{key === 'apiKeyId' ? (filters.apiKeyLabel || filters.apiKeyId) : filters[key]}</span>
                            <button
                                style={styles.chipClose}
                                onClick={() => updateFilters(key === 'apiKeyId' ? { apiKeyId: '', apiKeyLabel: '', page: 0 } : { [key]: '', page: 0 })}
                                aria-label={'Clear'.translate(lang)}
                            >&times;</button>
                        </div>
                    ))}
                </div>
            ) : null}

            {rangeTooLong ? <Alert color="warning">{`${'The range can be at most'.translate(lang)} ${MAX_RANGE_DAYS} ${'days'.translate(lang)}`}</Alert> : null}
            {error ? <Alert color="danger">{error}</Alert> : null}

            <div style={styles.tableCard}>
                <Table responsive hover>
                    <thead>
                        <tr>
                            <th
                                style={{ cursor: 'pointer' }}
                                onClick={() => updateFilters({ sortField: 'createdAt', sortType: filters.sortField === 'createdAt' && filters.sortType === 1 ? -1 : 1 })}
                            >
                                {'Time'.translate(lang)} {filters.sortField === 'createdAt' ? (filters.sortType === 1 ? '▲' : '▼') : ''}
                            </th>
                            {!clientId ? <th>{'Client'.translate(lang)}</th> : null}
                            <th>{'Key'.translate(lang)}</th>
                            <th>{'Method'.translate(lang)}</th>
                            <th>{'Path'.translate(lang)}</th>
                            <th
                                style={{ cursor: 'pointer' }}
                                onClick={() => updateFilters({ sortField: 'status', sortType: filters.sortField === 'status' && filters.sortType === 1 ? -1 : 1 })}
                            >
                                {'Status'.translate(lang)} {filters.sortField === 'status' ? (filters.sortType === 1 ? '▲' : '▼') : ''}
                            </th>
                            <th
                                style={{ cursor: 'pointer' }}
                                onClick={() => updateFilters({ sortField: 'durationMs', sortType: filters.sortField === 'durationMs' && filters.sortType === 1 ? -1 : 1 })}
                            >
                                {'Duration'.translate(lang)} {filters.sortField === 'durationMs' ? (filters.sortType === 1 ? '▲' : '▼') : ''}
                            </th>
                            <th title={'Points scale directly with duration, sort by Duration to sort by cost'.translate(lang)}>{'Points'.translate(lang)}</th>
                            <th
                                style={{ cursor: 'pointer' }}
                                onClick={() => updateFilters({ sortField: 'db.queries', sortType: filters.sortField === 'db.queries' && filters.sortType === 1 ? -1 : 1 })}
                            >
                                {'DB queries'.translate(lang)} {filters.sortField === 'db.queries' ? (filters.sortType === 1 ? '▲' : '▼') : ''}
                            </th>
                            <th
                                style={{ cursor: 'pointer' }}
                                onClick={() => updateFilters({ sortField: 'db.totalMs', sortType: filters.sortField === 'db.totalMs' && filters.sortType === 1 ? -1 : 1 })}
                            >
                                {'DB ms'.translate(lang)} {filters.sortField === 'db.totalMs' ? (filters.sortType === 1 ? '▲' : '▼') : ''}
                            </th>
                            <th>{'Auth'.translate(lang)}</th>
                            <th>{'Replica'.translate(lang)}</th>
                            <th>{'IP'.translate(lang)}</th>
                            <th>{'Request ID'.translate(lang)}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={clientId ? 13 : 14} style={styles.emptyState}>{'Loading...'.translate(lang)}</td></tr>
                        ) : !items.length ? (
                            <tr><td colSpan={clientId ? 13 : 14} style={styles.emptyState}>{'No requests in the selected period'.translate(lang)}</td></tr>
                        ) : items.map((log) => (
                            <tr key={log._id} style={{ cursor: 'pointer' }} onClick={() => setOpenLogId(log._id)}>
                                <td>{log.createdAt ? moment(log.createdAt).format('YYYY-MM-DD HH:mm:ss') : '-'}</td>
                                {!clientId ? (
                                    <td>
                                        {log.client ? (
                                            onOpenClient && (log.clientId || log.client._id) ? (
                                                <button
                                                    type="button"
                                                    style={{ background: 'transparent', border: 'none', padding: 0, color: '#33405E', textDecoration: 'underline', cursor: 'pointer' }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onOpenClient({ clientId: log.clientId || log.client._id, name: log.client.company || log.client.username, username: log.client.username, company: log.client.company, customerNumber: log.client.customerNumber });
                                                    }}
                                                >
                                                    {log.client.company || log.client.username}
                                                </button>
                                            ) : (log.client.company || log.client.username)
                                        ) : (
                                            // rejected before a client was resolved: who knocked, and why it was refused
                                            <span style={{ color: '#818FA5' }}>
                                                <span style={styles.mono}>{log.ip || '-'}</span>
                                                {log.authError ? <div style={{ fontSize: 11 }}>{typeof log.authError === 'object' ? log.authError.code || log.authError.message : log.authError}</div> : null}
                                            </span>
                                        )}
                                    </td>
                                ) : null}
                                <td>{log.apiKey ? log.apiKey.name : '-'}</td>
                                <td>{log.method}</td>
                                <td style={styles.mono}>{log.path || log.route}</td>
                                <td><span className={statusBadgeClass(log.status)}>{log.status}</span></td>
                                <td>{formatMs(log.durationMs)}</td>
                                <td>{log.costPoints !== undefined && log.costPoints !== null ? formatPoints(log.costPoints) : '-'}</td>
                                <td>{log.db ? formatNumber(log.db.queries) : '-'}</td>
                                <td>{log.db ? formatMs(log.db.totalMs) : '-'}</td>
                                <td>{log.authMethod || '-'}</td>
                                <td><ReplicaCell log={log} lang={lang} /></td>
                                <td style={styles.mono}>{log.ip || '-'}</td>
                                <td style={styles.mono}>
                                    {log.requestId ? (
                                        <>
                                            {log.requestId}
                                            <span style={{ ...styles.chip, marginLeft: 6, padding: '1px 6px' }}>{log.requestIdSource || '-'}</span>
                                            <CopyButton lang={lang} value={log.requestId} label="Copy request ID" />
                                        </>
                                    ) : '-'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>

                <div style={styles.footer}>
                    <span style={{ fontSize: 13, color: '#818FA5' }}>
                        {`${'Total'.translate(lang)}: ${formatNumber(total)}`}
                    </span>

                    <Label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#818FA5' }}>
                        {'Per page'.translate(lang)}
                        <Input
                            type="select"
                            style={{ minWidth: 70, height: 36 }}
                            value={filters.entries}
                            onChange={(e) => updateFilters({ entries: Math.min(MAX_LOGS_ENTRIES, Number(e.target.value)), page: 0 })}
                        >
                            {[25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                        </Input>
                    </Label>

                    <ReactPaginate
                        previousLabel={'<'}
                        nextLabel={'>'}
                        breakLabel={'...'}
                        pageCount={totalPages}
                        marginPagesDisplayed={1}
                        pageRangeDisplayed={2}
                        onPageChange={(p) => updateFilters({ page: p.selected })}
                        containerClassName={'pagination'}
                        pageClassName={'page-item'}
                        pageLinkClassName={'page-link'}
                        previousClassName={'page-item-next'}
                        previousLinkClassName={'page-link'}
                        nextClassName={'page-item-next'}
                        nextLinkClassName={'page-link'}
                        activeClassName={'active'}
                        forcePage={Math.max(0, Number(filters.page) || 0)}
                    />
                </div>
            </div>

            <ExternalApiLogDetail
                lang={lang}
                logId={openLogId}
                onClose={() => setOpenLogId(null)}
                onOpenLogs={(patch) => { setOpenLogId(null); applyFilterPatch({ ...patch, page: 0 }); }}
            />
        </div>
    );
}

export default ExternalApiLogs;
